import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'
import {
  breakpoint,
  fontSize,
  pxToRem,
  size,
} from '@twistopayments/design-system/styles'

export const table = (theme: Theme) => css`
  display: table;
  border-collapse: collapse;
  border-radius: 0 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm};
  border-spacing: 0;
  text-align: center;
  margin-top: 7em;
  margin-bottom: ${size[40]};
  color: #1a0826;
  width: 100%;
`

export const headline = (theme: Theme) => css`
  background-color: ${theme.palette.background.main};
  text-align: left;
  padding: 0 ${size[24]};
  width: 34%;
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.xl} 0 0;
  padding-block: ${size[24]};
  vertical-align: bottom;

  h3 {
    margin-top: 0;
    margin-bottom: ${size[4]};
    line-height: 1.1;
    font-size: ${pxToRem(32)};
    font-weight: 600;
    color: ${theme.palette.text.title};
  }
`

export const planHead = css`
  vertical-align: bottom;
  padding-bottom: ${size[24]};
  font-size: ${fontSize.smMd};
  font-weight: 400;
`

export const highlighted = (theme: Theme) => css`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 2px;
    height: calc(100% - 4px);
    right: -1px;
    bottom: 0;
    background: ${theme.palette.primary.maxContrast};
  }

  &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: calc(100% - 4px);
    left: -1px;
    bottom: 0;
    background: ${theme.palette.primary.maxContrast};
  }
`

export const name = css`
  h3 {
    margin: 0;
    font-size: ${pxToRem(32)};
    font-weight: 600;
  }
`

export const priceLabel = (theme: Theme) => css`
  margin-top: ${size[4]};
  margin-bottom: ${size[16]};
  color: ${theme.palette.tertiary.mediumContrastAlternative};
`

export const description = css`
  min-height: 4em;
  padding-inline: ${size[8]};
  margin: 0;

  ${breakpoint.up('md')} {
    min-height: 3em;
  }
`

export const gift = (theme: Theme) => css`
  background-color: ${theme.palette.primary.lowContrast};
  color: ${theme.palette.primary.mainContrast};
  padding: ${size[8]} ${size[12]};
  width: 100%;
  max-height: ${size[56]};
  margin: ${size[4]} auto 0;
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  gap: ${size[4]};
  position: relative;
  margin-bottom: ${size[8]};

  p {
    margin: 0;
    font-size: ${pxToRem(20)};
  }

  ${breakpoint.up('smMd')} {
    padding: ${size[12]};
    margin-block: ${size[16]};
    width: calc(100% - 8px);
  }

  ${breakpoint.up('md')} {
    width: calc(100% - 32px);
  }

  ${breakpoint.up('lg')} {
    margin-top: ${size[80]};
  }
`

export const extra = (theme: Theme) => css`
  background: ${theme.palette.primary.maxContrast};
  position: absolute;
  text-align: center;
  width: 100%;
  padding: 0.5em;
  color: ${theme.palette.background.main};
  border: 2px solid ${theme.palette.primary.maxContrast};
  left: 0;
  top: 0;
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 1rem;
  margin: 0;
`

export const checkmarkIcon = (theme: Theme) => css`
  svg {
    fill: ${theme.palette.primary.maxContrast};
  }
`

export const row = css`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 1px;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(42, 39, 46, 0) 0,
      rgba(42, 39, 46, 0.1) 20%,
      rgba(42, 39, 46, 0.1) 80%,
      rgba(42, 39, 46, 0) 100%
    );
    left: 0;
  }
`

export const lastRow = css`
  &::after {
    background: none;
  }
`

export const featureRowTooltip = css`
  h4,
  p {
    margin: 0;
  }

  h4 {
    font-size: ${fontSize.md};
    font-weight: 500;
  }
`

export const tooltipHint = css`
  color: #828b99;
`

export const featureColumn = (theme: Theme) => css`
  background-color: ${theme.palette.background.main};
  padding: 0 ${size[24]};
  width: 34%;
`

export const featureName = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const planColumn = css`
  padding: ${size[16]} ${size[4]};
  width: 22%;
`

export const highlightedColumn = (theme: Theme) => css`
  border-left: 2px solid ${theme.palette.primary.maxContrast};
  border-right: 2px solid ${theme.palette.primary.maxContrast};
`

export const highlightedColumnLast = (theme: Theme) => css`
  border-bottom: 2px solid ${theme.palette.primary.maxContrast};
`
